import { Box, useTheme } from '@material-ui/core';
import React, { FunctionComponent, useCallback } from 'react';
import useIsMobile from '../../../../../../common/react/useIsMobile';
import PriceWithLabel from '../../../../customiseContainer/footer/PriceSection/CarPrices/PriceWithLabel/PriceWithLabel';
import singleCurrencyStyles from './singleCurrencyStyles';
import IndicativeNumber from '../../../../../../common/components/indicativeNumber/IndicativeNumber';
import useScrollToSection from '../../../../../hooks/navigation/useScrollToSection';
import { CustomiseSections } from '../../../../../../common/enum/CustomiseSections';
import { ScrollInViewBehaviourType } from '../../../../../../common/service/scrollInView';
import CalculateButton from '../../../financeElements/calculateButton/CalculateButton';
import { IPaymentValue } from '../../../../../../gqlCommon/model/IPaymentValue';
import Translation from '../../../../../../gqlCommon/components/translation/Translation';
import { PriceDisplayType } from '../../../../../../../../common/enum/PriceDisplayType';
import InformativeSectionContainer from '../../../../customiseContainer/footer/PriceSection/informativeSection/InformativeSectionContainer';
import { ISpcTheme } from '../../../../../../common/theme/ISpcTheme';

export interface IFinanceSingleCurrency {
  loading?: boolean;
  paymentValue: IPaymentValue;
  paymentFrequency: string;
  onFinanceCalculate: () => void;
  comparisonRate: number | null;
  taxLabel?: string;
}

const SingleCurrency: FunctionComponent<IFinanceSingleCurrency> = ({
  loading,
  paymentValue,
  paymentFrequency,
  onFinanceCalculate,
  comparisonRate,
  taxLabel,
}) => {
  const isMobile = useIsMobile();
  const styles = singleCurrencyStyles();
  const { scrollToSection } = useScrollToSection();
  const onFinanceClick = useCallback(() => {
    scrollToSection(CustomiseSections.DISCLAIMER, ScrollInViewBehaviourType.SMOOTH);
  }, []);
  const {
    spc: {
      customise: { footer },
    },
  } = useTheme<ISpcTheme>();
  const isLargeAmountStyle = isMobile && footer?.largeAmountStyle;

  const showFrequencyLabel = useCallback(
    () => (
      <>
        <Translation
          id={
            comparisonRate ? paymentFrequency : 'trans__configurator__customise_page_finance_details__repayment_label'
          }
        />
        <IndicativeNumber
          dataTest="repayment"
          displayNumber={<Translation id="trans__configurator__indicative_numbers__repayment" />}
          onClick={onFinanceClick}
        />
      </>
    ),
    [comparisonRate, paymentFrequency],
  );

  return (
    <Box
      display="flex"
      flexDirection={isMobile && !footer?.largeAmountStyle ? 'column-reverse' : 'row'}
      alignItems={'center'}
      justifyContent={isLargeAmountStyle ? 'center' : 'flex-start'}
      width={1}
      data-test={'finance_values:price:payment_value'}
      className={styles.priceBoxContainer}
    >
      {isLargeAmountStyle && (
        <Box className={styles.informativeSection}>
          <InformativeSectionContainer />
        </Box>
      )}
      <PriceWithLabel
        priceDisplayType={PriceDisplayType.FINANCE}
        price={{
          money: {
            amount: paymentValue?.amount ?? 0,
            currency: paymentValue?.currency,
          },
        }}
        label={showFrequencyLabel()}
        loading={loading}
        taxLabel={taxLabel}
      />
      {!isLargeAmountStyle && (
        <Box className={styles.priceWithButtonSectionItem}>
          <CalculateButton disabled={loading} onFinanceCalculate={onFinanceCalculate} />
        </Box>
      )}
    </Box>
  );
};

export default SingleCurrency;
